import React from 'react'
import {
  BarChartOutlined,
  BankOutlined,
  MonitorOutlined,
  EnvironmentOutlined,
  MailOutlined,
  NotificationOutlined,
  CustomerServiceOutlined,
  ContainerOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  CarOutlined,
  SettingOutlined,
  DashboardOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
  HistoryOutlined,
  ToolOutlined,
  TruckOutlined,
  HomeOutlined,
  MessageOutlined,
  ApartmentOutlined,
  AlignLeftOutlined,
  RestOutlined,
  NodeIndexOutlined,
  FileDoneOutlined,
  SlidersOutlined,
} from '@ant-design/icons'
import { ItemType } from 'antd/lib/menu/hooks/useItems'

import { TSidebarOriginalItem } from 'src/components/layout/types'
import { routingMap } from 'src/reducers/routing/routing-map'
import { Role } from 'src/reducers/routing/utils'

export default function getItems(
  t: (str: string, props?: Record<string, unknown>) => string,
  roles: Role[],
  onLinkClick: (pathname: string, state?: Record<string, unknown>) => void
): ItemType[] {
  const items: TSidebarOriginalItem[] = [
    {
      key: 'dashboard',
      label: t('Data & Analytics'),
      icon: <DashboardOutlined />,
      children: [
        {
          title: t('Overview'),
          icon: <UnorderedListOutlined />,
          link: routingMap.app.dashboard._,
          visibleInTooltip: true,
        },
        {
          title: t('Dashboards'),
          icon: <BarChartOutlined />,
          link: routingMap.app.analytics.dashboards._,
          skipLinks: [routingMap.app.analytics.dashboards.create._],
          visibleInTooltip: true,
        },
        {
          title: t('Data Exports'),
          icon: <CloudDownloadOutlined />,
          link: routingMap.app.analytics.exports._,
          visibleInTooltip: true,
        },
        {
          title: t('Classic Exports'),
          icon: <FileTextOutlined />,
          link: routingMap.app.analytics['classic-exports']._,
          visibleInTooltip: true,
        },
        {
          title: t('Data Imports'),
          icon: <CloudUploadOutlined />,
          link: routingMap.app.analytics.imports._,
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'customer_management',
      label: t('Customers'),
      icon: <TeamOutlined />,
      children: [
        {
          title: t('Properties'),
          icon: <HomeOutlined />,
          link: routingMap.app['customer-management'].properties._,
          skipLinks: [
            routingMap.app['customer-management'].properties.map._,
            routingMap.app['customer-management'].properties.create._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Map View'),
          icon: <EnvironmentOutlined />,
          link: routingMap.app['customer-management'].properties.map._,
          skipLinks: [
            routingMap.app['customer-management'].properties.create._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Groups'),
          icon: <ApartmentOutlined />,
          link: routingMap.app['customer-management']['property-groups']._,
          skipLinks: [
            routingMap.app['customer-management']['property-groups'].create._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Contacts'),
          icon: <UserOutlined />,
          link: routingMap.app['customer-management'].contacts._,
          visibleInTooltip: true,
        },
        {
          title: t('SMS/Email Service'),
          icon: <MessageOutlined />,
          link: routingMap.app['customer-management'].subscriptions._,
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'tickets',
      label: t('Tickets'),
      icon: <CustomerServiceOutlined />,
      children: [
        {
          title: t('Overview'),
          icon: <FileDoneOutlined />,
          link: routingMap.app.tickets._,
          skipLinks: [
            routingMap.app.tickets.kanban._,
            routingMap.app.tickets.map._,
            routingMap.app.tickets['create-bulk'].containers._,
            routingMap.app.tickets['create-bulk'].properties._,
            routingMap.app.tickets['customer-inbox']._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Map View'),
          icon: <EnvironmentOutlined />,
          link: routingMap.app.tickets.map._,
          visibleInTooltip: true,
        },
        {
          title: t('Kanban'),
          icon: <AlignLeftOutlined rotate={90} />,
          link: routingMap.app.tickets.kanban._,
          visibleInTooltip: true,
        },
        {
          title: t('Customer Inbox'),
          icon: <MailOutlined />,
          link: routingMap.app.tickets['customer-inbox']._,
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'operation_management',
      label: t('Operations'),
      icon: <SettingOutlined />,
      children: [
        ...(roles.includes('live-monitoring')
          ? [
              {
                title: t('Live Monitoring'),
                icon: <MonitorOutlined />,
                link: routingMap.app['operation-management']['live-monitoring']
                  ._,
                visibleInTooltip: true,
              },
            ]
          : []),
        {
          title: t('Routes'),
          icon: <NodeIndexOutlined />,
          link: routingMap.app['operation-management'].routes._,
          visibleInTooltip: true,
        },
        {
          title: t('Route Schemes'),
          icon: <SlidersOutlined />,
          link: routingMap.app['operation-management']['route-schemes']._,
          skipLinks: [
            routingMap.app['operation-management']['route-schemes'].create._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Pickup History'),
          icon: <HistoryOutlined />,
          link: routingMap.app['operation-management']['pickup-history']._,
          visibleInTooltip: true,
        },
        {
          title: t('Weight Control'),
          icon: <ToolOutlined />,
          link: routingMap.app['operation-management']['weight-control']._,
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'fleet_management',
      label: t('Fleet'),
      icon: <TruckOutlined />,
      children: [
        {
          title: t('Vehicles'),
          icon: <CarOutlined />,
          link: routingMap.app['fleet-management'].vehicles._,
          skipLinks: [routingMap.app['fleet-management'].vehicles.create._],
          visibleInTooltip: true,
        },
        {
          title: t('Drivers'),
          icon: <TeamOutlined />,
          link: routingMap.app['fleet-management'].drivers._,
          skipLinks: [routingMap.app['fleet-management'].drivers.create._],
          visibleInTooltip: true,
        },
        {
          title: t('Locations'),
          icon: <BankOutlined />,
          link: routingMap.app['fleet-management'].locations._,
          skipLinks: [routingMap.app['fleet-management'].locations.create._],
          visibleInTooltip: true,
        },
        {
          title: t('Planning'),
          icon: <SettingOutlined />,
          link: routingMap.app['fleet-management']['vehicle-planner']._,
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'asset_management',
      label: t('Assets'),
      icon: <ContainerOutlined />,
      children: [
        {
          title: t('Containers'),
          icon: <RestOutlined />,
          link: routingMap.app['asset-management'].containers._,
          skipLinks: [
            routingMap.app['asset-management'].containers.map._,
            routingMap.app['asset-management'].containers.create._,
          ],
          visibleInTooltip: true,
        },
        {
          title: t('Map View'),
          icon: <EnvironmentOutlined />,
          link: routingMap.app['asset-management'].containers.map._,
          skipLinks: [routingMap.app['asset-management'].containers.create._],
          visibleInTooltip: true,
        },
        {
          title: t('Groups'),
          icon: <ApartmentOutlined />,
          link: routingMap.app['asset-management']['container-group']._,
          skipLinks: [
            routingMap.app['asset-management']['container-group'].create._,
          ],
          visibleInTooltip: true,
        },
      ],
    },
    {
      key: 'notifications',
      label: t('Alerts'),
      icon: <NotificationOutlined />,
      link: routingMap.app.notifications.alerts._,
      onClick: () => onLinkClick(routingMap.app.notifications.alerts._),
    },
  ]

  return items.map((item) => {
    if ('type' in item) {
      return item
    }

    if (item.onClick || item.link) {
      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
        onClick: item.onClick,
      }
    }

    if (!item.children) {
      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
      }
    }

    return {
      key: item.key,
      label: item.label,
      icon: item.icon,
      children: item.children.map((child) => ({
        key: child.link,
        label: child.title,
        icon: child.icon,
        onClick: () => onLinkClick(child.link, child.linkState),
      })),
    }
  })
}
